import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Eye, Search, Filter, Calendar,
  Building2, Smartphone, Printer, Wifi, Package, Monitor, Cpu,
  RefreshCw, AlertCircle, CheckCircle, Clock, Receipt, ClipboardCheck, ClipboardX, Trash2
} from 'lucide-react';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://ittoolbox.devkit.com.tr/api';

interface Document {
  id: number;
  name: string;
  type: 'printer' | 'mobile_phone' | 'circuit' | 'maintenance_contract' | 'invoice' | 'computer_assignment' | 'computer_return' | 'phone_assignment' | 'phone_return' | 'iot_assignment' | 'iot_return';
  category: string;
  file_path: string;
  filename: string;
  created_at: string;
  updated_at: string;
  supplier_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  status?: string;
  order_number?: string;
  total_amount?: number;
  currency?: string;
}

interface AssignmentForm {
  id: number;
  form_number: string;
  form_type: 'assignment' | 'return';
  mobile_phone_id?: number;
  handheld_terminal_id?: number;
  computer_id?: number;
  assigned_user_name: string;
  assigned_by_name: string;
  assignment_date: string;
  return_date?: string;
  signed_form_path?: string;
  created_at: string;
  inventory_number?: string;
  brand?: string;
  model?: string;
}

interface DocumentCategory {
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  documents: Document[];
}

const DocumentsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [assignmentForms, setAssignmentForms] = useState<AssignmentForm[]>([]);
  const [scrapForms, setScrapForms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showInvoices, setShowInvoices] = useState(false);
  const [showAssignmentForms, setShowAssignmentForms] = useState('');
  const [showScrapForms, setShowScrapForms] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Invoice pagination states
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoiceItemsPerPage] = useState(10);
  
  // Assignment forms pagination states
  const [assignmentCurrentPage, setAssignmentCurrentPage] = useState(1);
  const [assignmentItemsPerPage] = useState(10);

  // Scrap forms pagination states
  const [scrapCurrentPage, setScrapCurrentPage] = useState(1);
  const [scrapItemsPerPage] = useState(10);

  const documentTypes: Record<string, string> = {
    printer: language === 'en' ? 'Printer Contracts' : 'Yazıcı Sözleşmeleri',
    mobile_phone: language === 'en' ? 'Mobile Phone Contracts' : 'Cep Telefonu Sözleşmeleri',
    circuit: language === 'en' ? 'Circuit Contracts' : 'Devre Sözleşmeleri',
    maintenance_contract: language === 'en' ? 'Maintenance Agreements' : 'Bakım Anlaşmaları',
    maintenance_contract_file: language === 'en' ? 'Maintenance Agreement Files' : 'Bakım Anlaşması Dosyaları',
    invoice: language === 'en' ? 'Invoices' : 'Faturalar',
    computer_assignment: language === 'en' ? 'Computer Assignment Forms' : 'Bilgisayar Zimmet Formları',
    computer_return: language === 'en' ? 'Computer Return Forms' : 'Bilgisayar Geri Zimmet Formları',
    phone_assignment: language === 'en' ? 'Phone Assignment Forms' : 'Cep Telefonu Zimmet Formları',
    phone_return: language === 'en' ? 'Phone Return Forms' : 'Cep Telefonu Geri Zimmet Formları',
    iot_assignment: language === 'en' ? 'IoT Equipment Assignment Forms' : 'IoT Ekipman Zimmet Formları',
    iot_return: language === 'en' ? 'IoT Equipment Return Forms' : 'IoT Ekipman Geri Zimmet Formları',
    computer_scrap: language === 'en' ? 'Computer Scrap Reports' : 'Hurda Bilgisayar Raporları'
  };

  const typeIcons = {
    printer: Printer,
    mobile_phone: Smartphone,
    circuit: Wifi,
    maintenance_contract: Package,
    maintenance_contract_file: FileText,
    invoice: Receipt,
    computer_assignment: Monitor,
    computer_return: Monitor,
    phone_assignment: Smartphone,
    phone_return: Smartphone,
    iot_assignment: Cpu,
    iot_return: Cpu,
    computer_scrap: Trash2
  };

  const typeColors = {
    printer: 'bg-primary/20 text-primary-dark',
    mobile_phone: 'bg-green-100 text-green-800',
    circuit: 'bg-purple-100 text-purple-800',
    maintenance_contract: 'bg-orange-100 text-orange-800',
    maintenance_contract_file: 'bg-red-100 text-red-800',
    invoice: 'bg-yellow-100 text-yellow-800',
    computer_assignment: 'bg-cyan-100 text-cyan-800',
    computer_return: 'bg-teal-100 text-teal-800',
    phone_assignment: 'bg-indigo-100 text-indigo-800',
    phone_return: 'bg-pink-100 text-pink-800',
    iot_assignment: 'bg-sky-100 text-sky-800',
    iot_return: 'bg-rose-100 text-rose-800',
    computer_scrap: 'bg-stone-100 text-stone-800'
  };

  useEffect(() => {
    fetchDocuments();
    fetchInvoices();
    fetchAssignmentForms();
    fetchScrapForms();
  }, []);

  // Reset pagination when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
    setInvoiceCurrentPage(1);
    setAssignmentCurrentPage(1);
    setScrapCurrentPage(1);
  }, [searchTerm, showInvoices, showAssignmentForms, showScrapForms]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);

      const response = await fetch('/api/documents', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.data || []);
      }
    } catch (error) {
      console.error('Evraklar yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      // Maksimum fatura görmek için limit artırıldı ve pagination kaldırıldı
      const response = await apiService.getOrders({ limit: 1000 });
      // API response format kontrolü - güvenli array kontrolü
      let orders = [];
      if (Array.isArray(response)) {
        orders = response;
      } else if (response && Array.isArray(response.orders)) {
        orders = response.orders;
      } else if (response && Array.isArray(response.data)) {
        orders = response.data;
      } else {
        console.warn('Unexpected orders response format:', response);
        orders = [];
      }
      
      // Sadece fatura URL'si olan siparişleri göster
      const ordersWithInvoices = orders.filter((order: any) => {
        // İptal edilmiş siparişleri hariç tut
        if (order.status === 'iptal-edildi') {
          return false;
        }
        
        // Sadece fatura URL'si olan siparişleri dahil et
        return order.invoice_url && order.invoice_url.trim() !== '';
      });
      
      console.log(`Toplam sipariş: ${orders.length}, Fatura dosyası olan: ${ordersWithInvoices.length}`);
      setInvoices(ordersWithInvoices);
    } catch (error) {
      console.error('Faturalar yükleme hatası:', error);
    }
  };

  const fetchAssignmentForms = async () => {
    try {
      const response = await fetch('/api/assignment-forms', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Sadece imzalı formları göster
        const signedForms = data.filter((form: AssignmentForm) => form.signed_form_path);
        console.log(`Toplam zimmet formu: ${data.length}, İmzalı form: ${signedForms.length}`);
        setAssignmentForms(signedForms);
      }
    } catch (error) {
      console.error('Zimmet formları yükleme hatası:', error);
    }
  };

  const fetchScrapForms = async () => {
    try {
      const response = await fetch('/api/computers/scrapped/list', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Toplam hurda raporu: ${data.length}`);
        setScrapForms(data);
      }
    } catch (error) {
      console.error('Hurda raporları yükleme hatası:', error);
    }
  };

  const downloadInvoiceFromBackend = async (invoice: any) => {
    try {
      console.log('Fatura indiriliyor:', invoice.invoice_url);
      
      const response = await fetch(`/api/orders/${invoice.id}/invoice`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Dosya adını response header'ından al
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `Fatura_${invoice.order_number}`;
        
        if (contentDisposition) {
          // Önce standart filename="..." formatını kontrol et
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          } else {
            // URL encode edilmiş formatı kontrol et (filename*=UTF-8''...)
            const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
            if (filenameStarMatch) {
              filename = decodeURIComponent(filenameStarMatch[1]);
            } else {
              // Sadece filename= formatını kontrol et (tırnak işareti olmadan)
              const filenameSimpleMatch = contentDisposition.match(/filename=([^;]+)/);
              if (filenameSimpleMatch) {
                filename = filenameSimpleMatch[1].trim().replace(/^["']|["']$/g, '');
              }
            }
          }
        }
        
        // Dosya uzantısını invoice_url'den al (eğer header'da yoksa)
        if (!filename.includes('.')) {
          const urlPath = invoice.invoice_url || '';
          const urlExtension = urlPath.substring(urlPath.lastIndexOf('.'));
          if (urlExtension) {
            filename += urlExtension;
          } else {
            filename += '.pdf'; // Varsayılan olarak PDF
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log('Fatura başarıyla indirildi:', filename);
      } else {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || 'Fatura indirilemedi' };
        }
        console.error('Fatura indirme hatası:', errorData.error, response.status);
        alert(`Fatura indirilemedi: ${errorData.error || 'Bilinmeyen hata'}`);
      }
    } catch (error: any) {
      console.error('Fatura indirme hatası:', error);
      alert(`Fatura indirilirken hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    }
  };

  const downloadOrderAsPDF = async (invoice: any) => {
    try {
      // Backend'ten sipariş detaylarını al
      const response = await fetch(`/api/orders/${invoice.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const responseData = await response.json();
        const orderData = responseData.data || responseData;
        
        // HTML içeriği oluştur
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Sipariş Detayı - ${orderData.order_number}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #2c5aa0; padding-bottom: 20px; }
              .info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              .info-table th, .info-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              .info-table th { background-color: #f2f2f2; font-weight: bold; }
              .amount { font-size: 20px; font-weight: bold; color: #2c5aa0; }
              .status { padding: 4px 8px; border-radius: 4px; font-weight: bold; }
              .status-completed { background-color: #d4edda; color: #155724; }
              .status-pending { background-color: #fff3cd; color: #856404; }
              .status-cancelled { background-color: #f8d7da; color: #721c24; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Sipariş Detayı</h1>
              <h2>${orderData.order_number}</h2>
              <p>Oluşturulma Tarihi: ${new Date(orderData.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</p>
            </div>
            
            <table class="info-table">
              <tr>
                <th>Sipariş Numarası</th>
                <td>${orderData.order_number}</td>
              </tr>
              <tr>
                <th>Ticket Numarası</th>
                <td>${orderData.ticket_number}</td>
              </tr>
              <tr>
                <th>Şirket</th>
                <td>${orderData.company_name || 'N/A'}</td>
              </tr>
              <tr>
                <th>Lokasyon</th>
                <td>${orderData.location_name || 'N/A'}</td>
              </tr>
              <tr>
                <th>Tedarikçi</th>
                <td>${orderData.supplier_name || 'N/A'}</td>
              </tr>
              <tr>
                <th>Durum</th>
                <td><span class="status status-${orderData.status}">${orderData.status}</span></td>
              </tr>
              <tr>
                <th>Açıklama</th>
                <td>${orderData.description || 'N/A'}</td>
              </tr>
              <tr>
                <th>Toplam Tutar</th>
                <td class="amount">${orderData.total_amount} ${orderData.currency}</td>
              </tr>
              <tr>
                <th>Oluşturulma Tarihi</th>
                <td>${new Date(orderData.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</td>
              </tr>
              <tr>
                <th>Güncellenme Tarihi</th>
                <td>${new Date(orderData.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</td>
              </tr>
            </table>
          </body>
          </html>
        `;

        // Blob oluştur ve indir
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Siparis_${orderData.order_number}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

      } else {
        console.warn('Backend\'ten sipariş detayı alınamadı, mevcut veri kullanılıyor');
        // Backend'ten veri alınamazsa mevcut veriyi kullan
        downloadInvoiceAsPDF(invoice);
      }
    } catch (error) {
      console.error('Sipariş detayı indirme hatası:', error);
      // Hata durumunda mevcut veriyi kullan
      downloadInvoiceAsPDF(invoice);
    }
  };

  const downloadInvoiceAsPDF = (invoice: any) => {
    try {
      // Sipariş detaylarını PDF olarak oluştur
      const invoiceData = {
        orderNumber: invoice.order_number || 'N/A',
        ticketNumber: invoice.ticket_number || 'N/A',
        companyName: invoice.company_name || 'N/A',
        locationName: invoice.location_name || 'N/A',
        supplierName: invoice.supplier_name || 'N/A',
        totalAmount: invoice.total_amount || 0,
        currency: invoice.currency || 'TRY',
        status: invoice.status || 'N/A',
        description: invoice.description || 'N/A',
        createdAt: invoice.created_at || new Date().toISOString(),
        updatedAt: invoice.updated_at || new Date().toISOString()
      };

      // HTML içeriği oluştur
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Sipariş Detayı - ${invoiceData.orderNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .info-table th, .info-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .info-table th { background-color: #f2f2f2; }
            .amount { font-size: 18px; font-weight: bold; color: #2c5aa0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Sipariş Detayı</h1>
            <h2>${invoiceData.orderNumber}</h2>
          </div>
          
          <table class="info-table">
            <tr>
              <th>Sipariş Numarası</th>
              <td>${invoiceData.orderNumber}</td>
            </tr>
            <tr>
              <th>Ticket Numarası</th>
              <td>${invoiceData.ticketNumber}</td>
            </tr>
            <tr>
              <th>Şirket</th>
              <td>${invoiceData.companyName}</td>
            </tr>
            <tr>
              <th>Lokasyon</th>
              <td>${invoiceData.locationName}</td>
            </tr>
            <tr>
              <th>Tedarikçi</th>
              <td>${invoiceData.supplierName}</td>
            </tr>
            <tr>
              <th>Durum</th>
              <td>${invoiceData.status}</td>
            </tr>
            <tr>
              <th>Açıklama</th>
              <td>${invoiceData.description}</td>
            </tr>
            <tr>
              <th>Toplam Tutar</th>
              <td class="amount">${invoiceData.totalAmount} ${invoiceData.currency}</td>
            </tr>
            <tr>
              <th>Oluşturulma Tarihi</th>
              <td>${new Date(invoiceData.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</td>
            </tr>
            <tr>
              <th>Güncellenme Tarihi</th>
              <td>${new Date(invoiceData.updatedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</td>
            </tr>
          </table>
        </body>
        </html>
      `;

      // Blob oluştur ve indir
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Siparis_${invoiceData.orderNumber}_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      alert('Fatura indirilirken hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const downloadDocument = async (doc: Document) => {
    try {
      let downloadUrl = '';
      
      switch (doc.type) {
        case 'printer':
          downloadUrl = `/api/printers/${doc.id}/contract`;
          break;
        case 'mobile_phone':
          downloadUrl = `/api/mobile-phones/${doc.id}/contract`;
          break;
        case 'circuit':
          downloadUrl = `/api/circuits/${doc.id}/contract`;
          break;
        case 'maintenance_contract':
          downloadUrl = `/api/maintenance-contracts/${doc.id}/contract`;
          break;
        case 'maintenance_contract_file':
          downloadUrl = `/api/maintenance-contracts/files/${doc.id}/download`;
          break;
      }

      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.filename || doc.name;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        console.error('Dosya indirilemedi');
      }
    } catch (error) {
      console.error('Dosya indirme hatası:', error);
    }
  };

  const downloadAssignmentForm = async (form: AssignmentForm) => {
    try {
      if (!form.signed_form_path) {
        alert('İmzalı form bulunamadı');
        return;
      }

      const response = await fetch(`/api/assignment-forms/${form.id}/download-signed`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Content-Disposition header'ından dosya adını al
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${form.form_number}_imzali.pdf`;
        
        if (contentDisposition) {
          // filename="..." formatını kontrol et
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          } else {
            // filename*=UTF-8''... formatını kontrol et
            const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
            if (filenameStarMatch) {
              filename = decodeURIComponent(filenameStarMatch[1]);
            }
          }
        } else {
          // Header yoksa Content-Type'dan uzantıyı belirle
          const contentType = response.headers.get('Content-Type');
          let extension = '.pdf';
          
          if (contentType) {
            if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
              extension = '.jpg';
            } else if (contentType.includes('image/png')) {
              extension = '.png';
            } else if (contentType.includes('application/pdf')) {
              extension = '.pdf';
            }
          }
          
          filename = `${form.form_number}_imzali${extension}`;
        }
        
        console.log('Dosya indiriliyor:', filename);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        console.error('Zimmet formu indirilemedi');
        alert('Zimmet formu indirilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Zimmet formu indirme hatası:', error);
      alert('Zimmet formu indirilirken bir hata oluştu');
    }
  };

  const viewAssignmentForm = async (form: AssignmentForm) => {
    try {
      if (!form.signed_form_path) {
        alert('İmzalı form bulunamadı');
        return;
      }

      const response = await fetch(`/api/assignment-forms/${form.id}/download-signed`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        // Yeni sekmede aç
        window.open(url, '_blank');
        // URL'i bir süre sonra temizle (yeni sekme açıldıktan sonra)
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        console.error('Zimmet formu görüntülenemedi');
        alert('Zimmet formu görüntülenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Zimmet formu görüntüleme hatası:', error);
      alert('Zimmet formu görüntülenirken bir hata oluştu');
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'signed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-primary" />;
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'signed':
        return 'İmzalandı';
      case 'expired':
        return 'Süresi Dolmuş';
      case 'cancelled':
        return 'İptal Edilmiş';
      default:
        return 'Bilinmiyor';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = String(doc.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(doc.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || doc.category === selectedCategory;
    const matchesType = !selectedType || doc.type === selectedType;

    return matchesSearch && matchesCategory && matchesType;
  });

  const filteredInvoices = invoices.filter(invoice => {
    return String(invoice.order_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.supplier?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(invoice.description || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Zimmet formlarını kategorilere ayır
  const computerAssignmentForms = assignmentForms.filter(f => f.computer_id && f.form_type === 'assignment');
  const computerReturnForms = assignmentForms.filter(f => f.computer_id && f.form_type === 'return');
  const phoneAssignmentForms = assignmentForms.filter(f => f.mobile_phone_id && f.form_type === 'assignment');
  const phoneReturnForms = assignmentForms.filter(f => f.mobile_phone_id && f.form_type === 'return');
  const iotAssignmentForms = assignmentForms.filter(f => f.handheld_terminal_id && f.form_type === 'assignment');
  const iotReturnForms = assignmentForms.filter(f => f.handheld_terminal_id && f.form_type === 'return');

  // Filtrelenmiş zimmet formları
  const getFilteredAssignmentForms = () => {
    let forms: AssignmentForm[] = [];
    
    switch (showAssignmentForms) {
      case 'computer_assignment':
        forms = computerAssignmentForms;
        break;
      case 'computer_return':
        forms = computerReturnForms;
        break;
      case 'phone_assignment':
        forms = phoneAssignmentForms;
        break;
      case 'phone_return':
        forms = phoneReturnForms;
        break;
      case 'iot_assignment':
        forms = iotAssignmentForms;
        break;
      case 'iot_return':
        forms = iotReturnForms;
        break;
      default:
        return [];
    }

    return forms.filter(form => {
      const searchLower = searchTerm.toLowerCase();
      return (
        String(form.form_number || '').toLowerCase().includes(searchLower) ||
        String(form.assigned_user_name || '').toLowerCase().includes(searchLower) ||
        String(form.inventory_number || '').toLowerCase().includes(searchLower) ||
        String(form.brand || '').toLowerCase().includes(searchLower) ||
        String(form.model || '').toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredAssignmentForms = getFilteredAssignmentForms();

  // Sort invoices by date (newest first by default)
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    const dateA = new Date(a.updated_at).getTime();
    const dateB = new Date(b.updated_at).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Pagination logic for invoices
  const totalInvoices = sortedInvoices.length;
  const totalInvoicePages = Math.ceil(totalInvoices / invoiceItemsPerPage);
  const invoiceStartIndex = (invoiceCurrentPage - 1) * invoiceItemsPerPage;
  const invoiceEndIndex = invoiceStartIndex + invoiceItemsPerPage;
  const paginatedInvoices = sortedInvoices.slice(invoiceStartIndex, invoiceEndIndex);

  // Sort assignment forms by date (newest first by default)
  const sortedAssignmentForms = [...filteredAssignmentForms].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Pagination logic for assignment forms
  const totalAssignmentForms = sortedAssignmentForms.length;
  const totalAssignmentPages = Math.ceil(totalAssignmentForms / assignmentItemsPerPage);
  const assignmentStartIndex = (assignmentCurrentPage - 1) * assignmentItemsPerPage;
  const assignmentEndIndex = assignmentStartIndex + assignmentItemsPerPage;
  const paginatedAssignmentForms = sortedAssignmentForms.slice(assignmentStartIndex, assignmentEndIndex);

  // Filter and pagination for scrap forms
  const filteredScrapForms = scrapForms.filter(form => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      form.form_number?.toLowerCase().includes(search) ||
      form.inventory_number?.toLowerCase().includes(search) ||
      form.brand?.toLowerCase().includes(search) ||
      form.model?.toLowerCase().includes(search) ||
      form.serial_number?.toLowerCase().includes(search) ||
      form.created_by_user_name?.toLowerCase().includes(search)
    );
  });

  const sortedScrapForms = [...filteredScrapForms].sort((a, b) => {
    const dateA = new Date(a.scrap_date).getTime();
    const dateB = new Date(b.scrap_date).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalScrapForms = sortedScrapForms.length;
  const totalScrapPages = Math.ceil(totalScrapForms / scrapItemsPerPage);
  const scrapStartIndex = (scrapCurrentPage - 1) * scrapItemsPerPage;
  const scrapEndIndex = scrapStartIndex + scrapItemsPerPage;
  const paginatedScrapForms = sortedScrapForms.slice(scrapStartIndex, scrapEndIndex);

  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    if (!acc[doc.type]) {
      acc[doc.type] = [];
    }
    acc[doc.type].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('documents.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {t('documents.subtitle')}
          </p>
        </div>
        <button
          onClick={() => {
            fetchDocuments();
            fetchInvoices();
            fetchAssignmentForms();
          }}
          className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors min-h-[44px] w-full sm:w-auto"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Refresh' : 'Yenile'}
        </button>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arama
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={
                  showInvoices 
                    ? "Sipariş no, fatura ara..." 
                    : showAssignmentForms 
                    ? "Form no, kullanıcı, envanter no ara..." 
                    : "Evrak adı veya tedarikçi ara..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-base min-h-[44px]"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'en' ? 'Category' : 'Kategori'}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-base min-h-[44px]"
            >
              <option value="">{t('documents.allCategories')}</option>
              {Object.values(documentTypes).map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'en' ? 'Type' : 'Tür'}
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-base min-h-[44px]"
            >
              <option value="">{t('documents.allTypes')}</option>
              {Object.entries(documentTypes).map(([type, name]) => (
                <option key={type} value={type}>{name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        {Object.entries(documentTypes).map(([type, name]) => {
          let count = 0;
          if (type === 'invoice') {
            count = invoices.length;
          } else if (type === 'computer_assignment') {
            count = computerAssignmentForms.length;
          } else if (type === 'computer_return') {
            count = computerReturnForms.length;
          } else if (type === 'phone_assignment') {
            count = phoneAssignmentForms.length;
          } else if (type === 'phone_return') {
            count = phoneReturnForms.length;
          } else if (type === 'iot_assignment') {
            count = iotAssignmentForms.length;
          } else if (type === 'iot_return') {
            count = iotReturnForms.length;
          } else if (type === 'computer_scrap') {
            count = scrapForms.length;
          } else {
            count = documents.filter(doc => doc.type === type).length;
          }
          const IconComponent = typeIcons[type as keyof typeof typeIcons];
          const colorClass = typeColors[type as keyof typeof typeColors];

          const isAssignmentType = ['computer_assignment', 'computer_return', 'phone_assignment', 'phone_return', 'iot_assignment', 'iot_return'].includes(type);
          const isScrapType = type === 'computer_scrap';
          const isSelected = type === 'invoice' ? showInvoices : (isAssignmentType && showAssignmentForms === type) || (isScrapType && showScrapForms);

          return (
            <div
              key={type}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 cursor-pointer transition-all hover:shadow-md min-h-[80px] sm:min-h-[100px] ${
                isSelected ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => {
                if (type === 'invoice') {
                  setShowInvoices(!showInvoices);
                  setShowAssignmentForms('');
                  setShowScrapForms(false);
                  setSelectedType('');
                  setSelectedCategory('');
                  setSearchTerm('');
                } else if (isAssignmentType) {
                  setShowAssignmentForms(showAssignmentForms === type ? '' : type);
                  setShowInvoices(false);
                  setShowScrapForms(false);
                  setSelectedType('');
                  setSelectedCategory('');
                  setSearchTerm('');
                } else if (isScrapType) {
                  setShowScrapForms(!showScrapForms);
                  setShowInvoices(false);
                  setShowAssignmentForms('');
                  setSelectedType('');
                  setSelectedCategory('');
                  setSearchTerm('');
                } else {
                  setShowInvoices(false);
                  setShowAssignmentForms('');
                  setShowScrapForms(false);
                  setSelectedType(type);
                }
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between h-full">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 leading-tight">{name}</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{count}</p>
                  {type === 'invoice' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {count > 0 ? `${count} fatura dosyası mevcut` : 'Fatura dosyası bulunamadı'}
                    </p>
                  )}
                  {isAssignmentType && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {count > 0 ? `${count} imzalı form` : 'İmzalı form bulunamadı'}
                    </p>
                  )}
                  {isScrapType && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {count > 0 ? `${count} hurda raporu` : 'Hurda raporu bulunamadı'}
                    </p>
                  )}
                </div>
                <div className={`p-2 sm:p-3 rounded-full ${colorClass} self-start sm:self-auto mt-2 sm:mt-0`}>
                  <IconComponent className="w-4 h-4 sm:w-6 sm:h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Evraklar Listesi */}
      {showAssignmentForms ? (
        /* Zimmet Formları Görünümü */
        paginatedAssignmentForms.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <ClipboardCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Zimmet Formu Bulunamadı
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Arama kriterlerinize uygun zimmet formu bulunamadı.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${typeColors[showAssignmentForms as keyof typeof typeColors]} mr-3`}>
                  {(() => {
                    const IconComponent = typeIcons[showAssignmentForms as keyof typeof typeIcons];
                    return <IconComponent className="w-5 h-5" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {documentTypes[showAssignmentForms as keyof typeof documentTypes]}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {totalAssignmentForms} form ({paginatedAssignmentForms.length} gösteriliyor)
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700" style={{minWidth: '800px'}}>
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Form No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      Envanter No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      Marka/Model
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      Kullanıcı
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-20">
                      Tarih
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedAssignmentForms.map((form) => (
                    <tr key={`assignment-${form.id}`} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-32">
                        <div className="flex items-center">
                          <ClipboardCheck className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {form.form_number}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                          {form.inventory_number || '-'}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                          {form.brand} {form.model}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                          {form.assigned_user_name}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-20">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                          {new Date(form.form_type === 'return' && form.return_date ? form.return_date : form.assignment_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => downloadAssignmentForm(form)}
                            className="flex items-center px-2 py-1 text-xs sm:text-sm text-primary hover:text-primary-dark hover:bg-primary/10 dark:hover:bg-primary/20 rounded-md transition-colors min-h-[36px] whitespace-nowrap"
                            title={language === 'en' ? 'Download signed form' : 'İmzalı formu indir'}
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">{t('documents.download')}</span>
                          </button>
                          <button
                            onClick={() => viewAssignmentForm(form)}
                            className="flex items-center px-2 py-1 text-xs sm:text-sm text-green-600 hover:text-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors min-h-[36px] whitespace-nowrap"
                            title={language === 'en' ? 'View signed form' : 'İmzalı formu görüntüle'}
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">{t('documents.view')}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {language === 'en' ? 'Records per page' : 'Sayfa başına kayıt'}
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-sm min-h-[44px] flex items-center">
                      {language === 'en' ? '10 records (fixed)' : '10 kayıt (sabit)'}
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('documents.sortLabel')}
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm min-h-[44px]"
                    >
                      <option value="desc">{language === 'en' ? 'Newest → Oldest' : 'En yeni → En eski'}</option>
                      <option value="asc">{language === 'en' ? 'Oldest → Newest' : 'En eski → En yeni'}</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      {totalAssignmentForms > 0 ? `${assignmentStartIndex + 1}-${Math.min(assignmentEndIndex, totalAssignmentForms)} / ${totalAssignmentForms}` : '0 / 0'}
                    </span>
                  </div>
                  {totalAssignmentPages > 1 && (
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setAssignmentCurrentPage(Math.max(1, assignmentCurrentPage - 1))}
                        disabled={assignmentCurrentPage === 1}
                        className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        ←
                      </button>

                      <span className="px-3 py-2 text-sm min-h-[44px] flex items-center justify-center">
                        {assignmentCurrentPage} / {totalAssignmentPages}
                      </span>

                      <button
                        onClick={() => setAssignmentCurrentPage(Math.min(totalAssignmentPages, assignmentCurrentPage + 1))}
                        disabled={assignmentCurrentPage === totalAssignmentPages}
                        className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      ) : showInvoices ? (
        /* Faturalar Görünümü */
        paginatedInvoices.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {language === 'en' ? 'No Invoice Found' : 'Fatura Bulunamadı'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'en' ? 'No invoice found matching your search criteria.' : 'Arama kriterlerinize uygun fatura bulunamadı.'}
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-yellow-100 text-yellow-800 mr-3">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {language === 'en' ? 'Invoices' : 'Faturalar'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {totalInvoices} {language === 'en' ? 'invoice' : 'fatura'} ({paginatedInvoices.length} {language === 'en' ? 'shown' : 'gösteriliyor'})
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700" style={{minWidth: '800px'}}>
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      {language === 'en' ? 'Order No' : 'Sipariş No'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      {language === 'en' ? 'Supplier' : 'Tedarikçi'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-20">
                      {language === 'en' ? 'Date' : 'Tarih'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      {language === 'en' ? 'Amount' : 'Tutar'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-20">
                      {language === 'en' ? 'Status' : 'Durum'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32 hide-on-mobile">
                      {language === 'en' ? 'Description' : 'Açıklama'}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      {language === 'en' ? 'Actions' : 'İşlemler'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedInvoices.map((invoice) => (
                    <tr key={`invoice-${invoice.id}`} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-32">
                        <div className="flex items-center">
                          <Receipt className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {invoice.order_number}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                          {invoice.supplier?.name || '-'}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-20">
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                          {new Date(invoice.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <div className="text-xs sm:text-sm font-medium text-green-600">
                          {invoice.total_amount?.toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')} {invoice.currency}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-20">
                        <span className={`px-1 py-0.5 rounded-full text-xs whitespace-nowrap ${
                          invoice.status === 'fatura-onaylandi' || invoice.status === 'tamamlandi'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status === 'fatura-onaylandi' ? t('documents.statusApproved') :
                           invoice.status === 'tamamlandi' ? t('documents.statusCompleted') : t('documents.statusUploaded')}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 hide-on-mobile w-32">
                        {invoice.description && invoice.description.length > 15 ? (
                          <div className="relative group">
                            <span className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 cursor-default">
                              {invoice.description.slice(0, 15)}…
                            </span>
                            <div className="absolute left-0 bottom-full mb-1 z-50 hidden group-hover:block w-max max-w-xs bg-gray-900 dark:bg-gray-700 text-white text-xs rounded px-2 py-1 shadow-lg whitespace-pre-wrap break-words pointer-events-none">
                              {invoice.description}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                            {invoice.description || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                        <button
                          onClick={() => downloadInvoiceFromBackend(invoice)}
                          className="flex items-center px-2 py-1 text-xs sm:text-sm text-primary hover:text-primary-dark hover:bg-primary/10 dark:hover:bg-primary/20 rounded-md transition-colors min-h-[36px] whitespace-nowrap"
                        >
                          <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="hidden sm:inline">{t('documents.downloadInvoice')}</span>
                          <span className="sm:hidden">{t('documents.download')}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Tablonun altında */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {language === 'en' ? 'Records per page' : 'Sayfa başına kayıt'}
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 dark:bg-gray-600 text-sm min-h-[44px] flex items-center">
                      {language === 'en' ? '10 records (fixed)' : '10 kayıt (sabit)'}
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('documents.sortLabel')}
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm min-h-[44px]"
                    >
                      <option value="desc">{language === 'en' ? 'Newest → Oldest' : 'En yeni → En eski'}</option>
                      <option value="asc">{language === 'en' ? 'Oldest → Newest' : 'En eski → En yeni'}</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      {totalInvoices > 0 ? `${invoiceStartIndex + 1}-${Math.min(invoiceEndIndex, totalInvoices)} / ${totalInvoices}` : '0 / 0'}
                    </span>
                  </div>
                  {totalInvoicePages > 1 && (
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setInvoiceCurrentPage(Math.max(1, invoiceCurrentPage - 1))}
                        disabled={invoiceCurrentPage === 1}
                        className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        ←
                      </button>

                      <span className="px-3 py-2 text-sm min-h-[44px] flex items-center justify-center">
                        {invoiceCurrentPage} / {totalInvoicePages}
                      </span>

                      <button
                        onClick={() => setInvoiceCurrentPage(Math.min(totalInvoicePages, invoiceCurrentPage + 1))}
                        disabled={invoiceCurrentPage === totalInvoicePages}
                        className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      ) : showScrapForms ? (
        /* Hurda Raporları Görünümü */
        paginatedScrapForms.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Trash2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Hurda Raporu Bulunamadı
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Arama kriterlerinize uygun hurda raporu bulunamadı.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-stone-100 text-stone-800 mr-3">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Hurda Bilgisayar Raporları
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {totalScrapForms} rapor ({paginatedScrapForms.length} gösteriliyor)
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700" style={{minWidth: '800px'}}>
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Form No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Envanter No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      Marka / Model
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Seri No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Hurda Tarihi
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                      Hurdaya Ayıran
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                      İşlem
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedScrapForms.map((form: any) => (
                    <tr key={form.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                      <td className="px-2 sm:px-4 py-3 text-sm font-medium text-stone-600 dark:text-stone-400">
                        {form.form_number}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {form.inventory_number}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{form.brand}</div>
                        <div className="text-gray-500 dark:text-gray-400">{form.model}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {form.serial_number}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {new Date(form.scrap_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {form.created_by_user_name}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('navigate', { detail: 'scrap-computers' }));
                          }}
                          className="text-stone-600 hover:text-stone-900 flex items-center gap-1"
                          title="Detayları Görüntüle"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sıralama:</span>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm min-h-[44px]"
                  >
                    <option value="desc">En yeni → En eski</option>
                    <option value="asc">En eski → En yeni</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span>
                    {totalScrapForms > 0 ? `${scrapStartIndex + 1}-${Math.min(scrapEndIndex, totalScrapForms)} / ${totalScrapForms}` : '0 / 0'}
                  </span>
                </div>
                {totalScrapPages > 1 && (
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => setScrapCurrentPage(Math.max(1, scrapCurrentPage - 1))}
                      disabled={scrapCurrentPage === 1}
                      className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      ←
                    </button>

                    <span className="px-3 py-2 text-sm min-h-[44px] flex items-center justify-center">
                      {scrapCurrentPage} / {totalScrapPages}
                    </span>

                    <button
                      onClick={() => setScrapCurrentPage(Math.min(totalScrapPages, scrapCurrentPage + 1))}
                      disabled={scrapCurrentPage === totalScrapPages}
                      className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
        /* Normal Evraklar Görünümü */
        Object.keys(groupedDocuments).length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Evrak Bulunamadı
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Arama kriterlerinize uygun evrak bulunamadı.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedDocuments).map(([type, docs]) => {
              const IconComponent = typeIcons[type as keyof typeof typeIcons];
              const colorClass = typeColors[type as keyof typeof typeColors];

              return (
                <div key={type} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg ${colorClass} mr-3`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {documentTypes[type as keyof typeof documentTypes]}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {docs.length} evrak
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700" style={{minWidth: '600px'}}>
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-48">
                            Evrak Adı
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-32">
                            Tedarikçi
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-20">
                            Tarih
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                            Durum
                          </th>
                          <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider w-24">
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {docs.map((doc) => (
                          <tr key={`${doc.type}-${doc.id}`} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                            <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-48">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {doc.name}
                                </div>
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-32">
                              <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                                {doc.supplier_name || '-'}
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-20">
                              <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                                {new Date(doc.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                              {doc.status ? (
                                <div className="flex items-center">
                                  {getStatusIcon(doc.status)}
                                  <span className="ml-1 text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                                    {getStatusText(doc.status)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-3 whitespace-nowrap w-24">
                              <button
                                onClick={() => downloadDocument(doc)}
                                className="flex items-center px-2 py-1 text-xs sm:text-sm text-primary hover:text-primary-dark hover:bg-primary/10 dark:hover:bg-primary/20 rounded-md transition-colors min-h-[36px] whitespace-nowrap"
                              >
                                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                İndir
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default DocumentsPage;
